import { useMemo, useState, type FormEvent } from "react";
import { AnimatePresence, motion } from "motion/react";
import { applyOperations, clientCurrencies, createInvoiceDraft, earlierUninvoiced, entryDuration, invoiceDocument, isLive, makeId, uninvoicedByCurrency } from "@otrack/domain";
import { Button, DateField, Field, FieldError, Icon, Modal, Segmented, Select } from "@otrack/ui";
import { money } from "./components";
import { useApp, useDataset, useFactory } from "./context";
import { navigate } from "./router";
import { formatDuration, lastMonth } from "./time";

export function NewInvoice({ clientId: preset, onClose }: { clientId: string | null; onClose: () => void }) {
  const { now, act } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const clients = Object.values(dataset.clients).filter((client) => isLive(client) && (client.archivedAt === null || client.id === preset)).sort((a, b) => a.name.localeCompare(b.name));
  const [clientId, setClientId] = useState(preset ?? clients[0]?.id ?? "");
  const currencies = clientId ? clientCurrencies(dataset, clientId) : [];
  const [picked, setPicked] = useState<string | null>(null);
  const currency = picked && currencies.includes(picked) ? picked : currencies[0] ?? dataset.settings.defaultCurrency;
  const initial = lastMonth(now);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [error, setError] = useState<string | null>(null);

  // A throwaway Draft shows exactly what creating one would pick up.
  const preview = useMemo(() => {
    if (!clientId) return null;
    const id = "preview";
    const draft = applyOperations(dataset, createInvoiceDraft(dataset, { clientId, currency, periodFrom: Math.min(from, to), periodTo: Math.max(from, to) }, factory, id));
    const invoice = draft.invoices[id];
    if (!invoice) return null;
    const entries = Object.values(draft.timeEntries).filter((entry) => entry.invoiceId === id);
    return {
      entries: entries.length,
      ms: entries.reduce((sum, entry) => sum + entryDuration(entry, now), 0),
      fixed: invoice.fixedLines.length,
      subtotal: invoiceDocument(draft, invoice).totals.subtotal,
      earlier: earlierUninvoiced(draft, invoice).entries.length
    };
  }, [dataset, factory, clientId, currency, from, to, now]);

  const create = async (event: FormEvent) => {
    event.preventDefault();
    if (!clientId) return setError("Choose who the invoice is for.");
    const id = makeId("invoice");
    await act(createInvoiceDraft(dataset, { clientId, currency, periodFrom: Math.min(from, to), periodTo: Math.max(from, to) }, factory, id), `Draft invoice for ${dataset.clients[clientId]?.name ?? "client"} created`);
    onClose();
    navigate({ page: "invoice", id });
  };

  return <Modal title="New invoice" icon="receipt" description="It starts as a draft with all uninvoiced work in the period. Nothing is final until you mark it sent." onClose={onClose}>
    {clients.length === 0 ? <div className="ot-form">
      <p className="ot-form__help">Invoices are addressed to a client. Create one first, then come back.</p>
      <div className="ot-form__actions"><span className="ot-form__spacer"/><Button onClick={onClose}>Close</Button></div>
    </div> : <form className="ot-form" onSubmit={(event) => void create(event)} noValidate>
      <Field label="Client">
        <Select
          value={clientId}
          searchable={clients.length > 7}
          searchPlaceholder="Find a client"
          onChange={(next) => { setClientId(next); setPicked(null); setError(null); }}
          options={clients.map((client) => {
            const owed = Object.entries(uninvoicedByCurrency(dataset, client.id))[0];
            return { value: client.id, label: client.name, icon: "briefcase" as const, hint: owed ? `${money(owed[1], owed[0])} uninvoiced` : undefined };
          })}
        />
      </Field>
      <AnimatePresence initial={false}>
        {currencies.length > 1 && <motion.div key="currency" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }} style={{ overflow: "hidden" }}>
          <Field label="Currency" hint="One invoice per currency. ontrack never converts between them.">
            <Segmented stretch value={currency} onChange={setPicked} options={currencies.map((code) => ({ value: code, label: code }))}/>
          </Field>
        </motion.div>}
      </AnimatePresence>
      <div className="ot-form__pair">
        <Field label="From"><DateField value={from} onChange={setFrom}/></Field>
        <Field label="To"><DateField value={to} onChange={setTo}/></Field>
      </div>
      {preview && <div className="ot-form__card ot-new-invoice__preview">
        <div className="ot-new-invoice__figure">
          <span>Subtotal before tax</span>
          <strong className="ot-mono">{money(preview.subtotal, currency)}</strong>
        </div>
        <ul className="ot-new-invoice__facts">
          <li><Icon name="clock"/>{preview.entries ? `${preview.entries} ${preview.entries === 1 ? "entry" : "entries"}, ${formatDuration(preview.ms)}` : "No uninvoiced time in this period"}</li>
          {preview.fixed > 0 && <li><Icon name="coins"/>{preview.fixed} finished fixed price {preview.fixed === 1 ? "item" : "items"}</li>}
          {preview.earlier > 0 && <li className="is-muted"><Icon name="info"/>{preview.earlier} earlier uninvoiced {preview.earlier === 1 ? "entry" : "entries"} you can include from the draft</li>}
        </ul>
      </div>}
      <FieldError>{error}</FieldError>
      <div className="ot-form__actions">
        <span className="ot-form__spacer"/>
        <Button type="button" onClick={onClose}>Cancel</Button>
        <Button type="submit" tone="primary" icon="receipt">Create draft</Button>
      </div>
    </form>}
  </Modal>;
}
